import type { MonsterDef } from '../../sim/registry';
import { monsterStats } from '../balance';
import { circle, cone, rect, ring } from '../classes/kit';
import { mAreas, mCharge, mCombo, mLunge, mMelee, mRoar, mSkill, mVolley } from './kit';

/**
 * Sandsworn Tomb (Act II dungeon, levels 17–19). Reuses `sunscar_skeleton` and
 * `sand_golem` from Sunscar. The Sandbound Warden wears a Sand Shell (a
 * breakable part + the `warden_shell` damage-reduction status) that its boss
 * script removes when the part breaks: src/content/bosses/story.ts.
 */

const SAND = 0xd8b070;

export const TOMB_MONSTERS: MonsterDef[] = [
  {
    id: 'tomb_ghoul', name: 'Tomb Ghoul', grade: 'normal', model: 'mob_ghoul', scale: 1, radius: 0.5, tint: 0xcdb892, creature: 'Undead',
    stats: (l) => monsterStats(l, 'normal', { hp: 0.9, speed: 1.2 }),
    skills: [
      mMelee('tghoul_bite', 'Grave Bite', { shape: cone(2, 60), coef: 1, windup: 0.3, cd: 1.6 }),
      mCombo('tghoul_rend', 'Rending Claws', {
        hits: [
          { at: 0.3, shape: cone(2.2, 80), coef: 0.5 },
          { at: 0.5, shape: cone(2.2, 80), coef: 0.5 },
          { at: 0.75, shape: cone(2.4, 90), coef: 0.8, status: [{ id: 'bleed', duration: 4, value: 0.12 }] },
        ],
        cd: 4.5,
      }),
      mLunge('tghoul_leap', 'Carrion Leap', { distance: 5, coef: 1.4, windup: 0.45, cd: 7, range: [2.5, 6] }),
    ],
    brain: 'melee', aggroRange: 10, leash: 22, xp: 55, loot: 'normal', weakness: 'holy',
  },
  {
    id: 'sandbound_warden', name: 'Sandbound Warden', grade: 'boss', model: 'boss_warden', scale: 1, radius: 1.6, tint: 0xd6b27a, creature: 'Construct',
    title: 'Keeper of the Sandsworn Tomb', stagger: 700, hpBars: 36, weakness: 'water',
    parts: [{ id: 'sand_shell', name: 'Sand Shell', durability: 14 }],
    stats: (l) => monsterStats(l, 'boss', { hp: 1, speed: 0.95 }),
    skills: [
      mAreas('warden_cleave', 'Sandsteel Cleave', { steps: [{ shape: cone(5.5, 140), delay: 0.8, coef: 2.8 }], cd: 4, range: [0, 5], anim: 'Attack', hue: SAND }),
      mAreas('warden_pillar_slam', 'Pillar Slam', {
        steps: [
          { shape: circle(3.2, 3), delay: 0.9, coef: 3 },
          { shape: ring(3.2, 6.5, 3), delay: 1.4, coef: 1.6, reaction: { kind: 'knockback', power: 2 } },
        ],
        cd: 5, range: [0, 5.5], hue: SAND,
      }),
      // Counterable overhead: a counter during the glow cancels the blade.
      mAreas('warden_judgement', 'Judgement Blade', {
        steps: [{ shape: rect(9, 3), delay: 1.35, coef: 4.2, reaction: { kind: 'knockdown', duration: 1.4, power: 1 }, cancelOnInterrupt: true }],
        counter: [0.55, 0.8], cd: 12, range: [0, 8], anim: 'Slam', hue: SAND,
      }),
      mCharge('warden_rush', 'Sandsteel Rush', { distance: 14, width: 3, windup: 1.25, coef: 3.8, count: 3, counterable: true, cd: 15, range: [4, 14], hue: SAND }),
      // Donut then inner circle: stand close for the storm, then step out.
      mAreas('warden_sandstorm', 'Sandstorm Veil', {
        steps: [
          { shape: ring(3.8, 13), delay: 1.3, coef: 3 },
          { shape: circle(3.8), delay: 2.4, coef: 3.2 },
        ],
        cd: 18, range: [0, 10], anim: 'Cast', hue: SAND,
      }),
      // Rolling boulders along five marked lanes.
      mVolley('warden_boulders', 'Rolling Boulders', {
        coef: 2.6, count: 5, spread: 80, speed: 9, reach: 24, radius: 1.2, pierce: true, windup: 1, lane: 2.6,
        reaction: { kind: 'knockdown', duration: 1.2, power: 2 }, cd: 14, range: [0, 20], anim: 'Slam', hue: SAND,
      }),
      mAreas('warden_quicksand', 'Quicksand', {
        steps: [{ shape: circle(4), anchor: 'point', delay: 0.9, coef: 0.3, duration: 6, tick: 1, reaction: { kind: 'none' }, status: [{ id: 'clay', duration: 2 }] }],
        cd: 12, range: [3, 18], maxRange: 18, anim: 'Cast', hue: SAND,
      }),
      // Mechanic (50% / 20%): cardinal lanes, then the diagonals.
      mAreas('warden_collapse', 'Tomb Collapse', {
        announce: 'The tomb collapses! Watch the falling stone!',
        steps: [
          ...[0, 90, 180, 270].map((rot) => ({ shape: rect(22, 4.5, 0, rot), delay: 1.6, coef: 4 })),
          ...[45, 135, 225, 315].map((rot) => ({ shape: rect(22, 4.5, 0, rot), delay: 3, coef: 4 })),
        ],
        cd: 40, range: [0, 99], superArmor: 'full', anim: 'Roar', hue: SAND,
      }),
      // Played by the script when the Sand Shell breaks: a helpless stumble.
      mSkill('warden_shatter', 'Shell Shattered', {
        announce: 'The Sand Shell shatters! The Warden reels!', cd: 1, range: [0, 99], superArmor: 'full', anim: 'Stagger', duration: 2.6, actions: [],
      }),
      mRoar('warden_awaken', 'Tomb Awakening', {
        announce: 'The Sandbound Warden awakens!', announceStyle: 'boss', duration: 1.6, cd: 60,
        nova: { shape: circle(6), delay: 1, coef: 1, telegraph: 'yellow', reaction: { kind: 'knockback', power: 3.5 } },
      }),
    ],
    brain: 'boss', aggroRange: 16, leash: 60, xp: 2400, loot: 'story_boss',
  },
];
